// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/modals/AddWhatsappModal/Step3.jsx
// *******************************************************************************
// Step 3: Confirmation / summary — shown after connection or save.
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Descriptions, Grid, Result } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { parsePhoneToDisplay } from 'utils/helpers';
import translateX from 'utils/translateX';
import WhatsAppSourceTag from '../../../../components/WhatsAppSourceTag';

// *******************************************************************************
// STEP CONTENT COMPONENT (needs hooks for breakpoint)
// *******************************************************************************
const Step3Content = ({ source, connectionStatus, displayName, phoneNumber }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.sm;
  const isBaileysFailure = source === 'baileys' && connectionStatus !== 'connected';
  const formattedPhone = formatPhone(phoneNumber);
  const sourceType = source === 'meta' ? 'meta' : 'baileys';

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  if (isBaileysFailure) {
    return (
      <Result
        icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        title={translateX('connection_not_established')}
        subTitle={translateX('connection_failed_description')}
      />
    );
  }

  return (
    <Result
      icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
      title={translateX('phone_added_successfully')}
      subTitle={
        <Descriptions
          column={1}
          bordered
          layout={isMobile ? 'vertical' : 'horizontal'}
          size="small"
          colon={false}
          className="mt-4"
          styles={isMobile ? undefined : { label: { width: 80, fontWeight: 500 } }}
          style={{ textAlign: 'left', marginTop: 8 }}
        >
          <Descriptions.Item label={translateX('name')}>{displayName || '—'}</Descriptions.Item>
          <Descriptions.Item label={translateX('phone')}>{formattedPhone || '—'}</Descriptions.Item>
          <Descriptions.Item label={translateX('origin')}>
            <WhatsAppSourceTag sourceType={sourceType} size="small" />
          </Descriptions.Item>
        </Descriptions>
      }
    />
  );
};

// *******************************************************************************
// STEP BUILDER
// *******************************************************************************
const step3 = ({ source, connectionStatus, displayName, phoneNumber } = {}) => ({
  stepTitle: translateX('summary'),
  stepTooltip: 'connection_result',
  content: () => (
    <Step3Content
      source={source}
      connectionStatus={connectionStatus}
      displayName={displayName}
      phoneNumber={phoneNumber}
    />
  ),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step3;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Format phone number for display — handles with/without country code
function formatPhone(phone) {
  if (!phone) return null;
  const str = String(phone).trim();
  if (str.startsWith('+')) {
    return parsePhoneToDisplay(str, undefined, { showCountryCode: true });
  }
  const formatted = parsePhoneToDisplay(`+${str}`, undefined, { showCountryCode: true });
  if (formatted && formatted !== `+${str}`) return formatted;
  return parsePhoneToDisplay(str, 'BR', { showCountryCode: true });
}
